import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import api from "../../utils/api";

/**
 * Lists future available booking slots created by the logged-in business owner,
 * with remove (DELETE) — used e.g. after external-site bookings.
 */
const MyOpenSlots = ({ refreshTrigger }) => {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [removingId, setRemovingId] = useState(null);
  const [confirmingId, setConfirmingId] = useState(null);
  const [inlineError, setInlineError] = useState("");

  const fetchSlots = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get(
        "/bookingslots?user=me&status=available&future=true"
      );
      setSlots(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("MyOpenSlots fetch:", err);
      setError("Could not load your openings.");
      setSlots([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSlots();
  }, [refreshTrigger, fetchSlots]);

  /**
   * Confirmation happens in the page, not in a native dialog.
   *
   * This used to call window.confirm(). That is browser chrome rather than a
   * DOM element, so anything driving the page by reading and clicking it --
   * an assistant, a test, an accessibility tool -- could never reach the OK
   * button, and the handler returned before sending anything. The opening
   * looked unremovable while the button and the route were both fine.
   */
  const handleRemove = (slot) => {
    setInlineError("");
    setConfirmingId(slot._id);
  };

  const cancelRemove = () => setConfirmingId(null);

  const confirmRemove = async (slot) => {
    setInlineError("");
    setRemovingId(slot._id);
    try {
      await api.delete(`/bookingslots/${slot._id}`);
      setSlots((prev) => prev.filter((s) => s._id !== slot._id));
      setConfirmingId(null);
    } catch (err) {
      const msg =
        err.response?.data?.msg ||
        err.response?.statusText ||
        "Could not remove this opening.";
      setInlineError(msg);
      console.error("MyOpenSlots delete:", err.response?.data || err.message);
      // The row stays in its confirming state so the person can try again or
      // back out, rather than silently reverting and looking like nothing
      // happened.
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div className="border-8 border-grey mt-3 mb-3">
      <p className="text-center text-2m bg-red text-white px-3 card-head font-semibold m-0">
        Your Upcoming Openings
      </p>
      <div className="p-3">
        {!loading && slots.length > 0 && (
          <p className="text-muted small mb-3">
            Open slots you added (available). Remove one if a customer booked
            elsewhere (e.g. external link).
          </p>
        )}
        {loading && (
          <p className="text-center text-muted small mb-0">Loading...</p>
        )}
        {!loading && error && (
          <div className="alert alert-danger mb-0">{error}</div>
        )}
        {inlineError && (
          <div className="alert alert-warning">{inlineError}</div>
        )}
        {!loading && !error && slots.length === 0 && (
          <p className="text-muted text-center mb-0">No upcoming openings.</p>
        )}
        {!loading && slots.length > 0 && (
          <div className="table-responsive">
            <table className="table table-sm dashboard-table mb-0">
              <thead>
                <tr>
                  <th>Business</th>
                  <th>Service</th>
                  <th>When</th>
                  <th></th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {slots.map((slot) => (
                  <tr key={slot._id}>
                    <td className="align-middle">
                      {slot.companyInfo?.name || "—"}
                    </td>
                    <td className="align-middle">
                      {slot.serviceInfo?.name || "—"}
                    </td>
                    <td className="align-middle">
                      {slot.start_time
                        ? moment(slot.start_time).format("MMM D, YYYY h:mm a")
                        : "—"}
                    </td>
                    <td className="align-middle small">
                      {slot.serviceInfo?.book_site ? (
                        <span className="badge badge-secondary">External link</span>
                      ) : null}
                    </td>
                    <td className="align-middle text-right">
                      {confirmingId === slot._id ? (
                        <span className="d-inline-flex align-items-center">
                          <span className="small text-muted mr-2">
                            Remove this opening?
                          </span>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger mr-1"
                            disabled={removingId === slot._id}
                            onClick={() => confirmRemove(slot)}
                          >
                            {removingId === slot._id ? "Removing…" : "Confirm"}
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            disabled={removingId === slot._id}
                            onClick={cancelRemove}
                          >
                            Cancel
                          </button>
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleRemove(slot)}
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

MyOpenSlots.propTypes = {
  refreshTrigger: PropTypes.number.isRequired,
};

export default MyOpenSlots;
